import { useGraphQLQuery } from '@betterment/graphql';
import { Box, Card, H3, Icon, Text } from '@betterment/samba-react';
import { AccountSettingsModal, CardLoader, ErrorBoundary, SambaAutoLink } from '@betterment/shared-components';
import { ThemedImage } from '@betterment/shared-components/src/components/themed-image/ThemedImage';
import { useRequiredParam } from '@betterment/shared-components/src/hooks/useRequiredParam';

import { CashAccountOverview } from './cash-account-overview/CashAccountOverview';
import { DirectTradingAccountOverview } from './direct-trading-account-overview/DirectTradingAccountOverview';
import { EnvelopeAccountHeaderDocument } from './graphql/envelopeAccountHeader.generated';
import { useEnvelopeAccountProjections } from './managed-account-overview/hooks/useEnvelopeAccountProjections';
import { useEnvelopePerformanceHistory } from './managed-account-overview/hooks/useEnvelopePerformanceHistory';
import { ManagedAccountOverview } from './managed-account-overview/ManagedAccountOverview';

import { IdempotencyProviderWithFeatureRegistry } from '../../../RetailApp/graphql/idempotency/IdempotencyProviderWithFeatureRegistry';
import { urlBuilder } from '../../../Shared/urlBuilder';
import { AccountSettingsModalWrapper } from '../settings/AccountSettingsModalWrapper';

export const OverviewCard = () => {
  const accountId = useRequiredParam('accountId');

  const { data } = useGraphQLQuery(EnvelopeAccountHeaderDocument, {
    variables: { id: accountId },
    throwOnError: true,
  });

  useEnvelopeAccountProjections();
  useEnvelopePerformanceHistory();

  if (!data) return <CardLoader />;
  if (!data.account || !('name' in data.account)) return null;

  const { account } = data;
  const purpose = account.envelope?.purpose;

  const overviewPerformanceAndProjection = () => {
    switch (account.__typename) {
      case 'ManagedAccount':
        return <ManagedAccountOverview />;
      case 'CashAccount':
        return (
          <CashAccountOverview
            accountId={accountId}
            apy={account.apy}
            balance={account.balance}
            fdicInsurance={account.fdicInsurance}
            performanceTotals={account.performanceTotals}
            legacyGoalId={account.legacyGoalId}
          />
        );
      case 'DirectTradingAccount':
        return <DirectTradingAccountOverview />;
      default:
        return null;
    }
  };

  return (
    <Card css={{ display: 'grid', gap: 'space.300', padding: 'space.300' }}>
      {purpose && (
        <Card.Header>
          <SambaAutoLink to={urlBuilder.appPurpose.absolute({ purposeId: purpose.id })}>
            <Box css={{ display: 'flex', alignItems: 'center', color: 'text' }}>
              <Icon name="CaretLeft" size="small" css={{ mr: 'space.100' }} />
              <Text>{purpose.name}</Text>
            </Box>
          </SambaAutoLink>
        </Card.Header>
      )}
      <Card.Content css={{ gap: 'space.300', display: 'grid' }}>
        <Box css={{ display: 'flex', gap: 'space.200', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box css={{ display: 'flex', gap: 'space.150' }}>
            <ThemedImage
              themedAsset={account.icon}
              alt={account.name}
              css={{ width: 'size.450', height: 'size.450' }}
            />
            <H3 variant="title300">{account.name}</H3>
          </Box>
          <IdempotencyProviderWithFeatureRegistry featureName="deletePurpose">
            <AccountSettingsModal>
              <ErrorBoundary fallbackType="partial">
                <AccountSettingsModalWrapper />
              </ErrorBoundary>
            </AccountSettingsModal>
          </IdempotencyProviderWithFeatureRegistry>
        </Box>
        {overviewPerformanceAndProjection()}
      </Card.Content>
    </Card>
  );
};
